// Conquer the Flag in the room: each side's team and army ability next to its player (a human player
// picks their own; the host picks for a bot or an empty seat), a summary of the map, and the host's
// other settings behind "All settings".
import React from 'react';
import CtfSettings, { TeamPicker } from '../games/capture-the-flag/CtfSettings';
import { BUILT_IN_TEAMS, loadSavedTeams } from '../games/capture-the-flag/teamLibrary';
import type { SavedTeam } from '../games/capture-the-flag/teamLibrary';
import { decodeTeam } from '../../games/capture-the-flag/teamcode';
import { ABILITY_NAMES, abilityText, EFFECT_NAMES, OUTPOST_NAMES, DEFAULT_CONFIG } from '../../games/capture-the-flag/capture-the-flag';
import { CTF_ROOM_DEFAULT_CONFIG } from '../../games/capture-the-flag/room';
import type { CtfRoomConfig } from '../../games/capture-the-flag/room';
import type { AbilityId, EffectId } from '../../games/capture-the-flag/types';
import type { ParticipantView, RoomView } from '../../types/room';
import { leagueOfRoom } from './leagueRoom';
import { gameOptionsText } from '../league/parts';
import { ArmyTable } from '../league/ArmyTable';
import type { LeaguePlayer } from '../../leagues/api';

type Side = 'A' | 'B';
const SIDE_NAME: Record<Side, string> = { A: 'Blue', B: 'Red' };
const SIDE_COLOR: Record<Side, string> = { A: 'var(--blue)', B: 'var(--red)' };

// A player's own picks, kept in room.seatConfig under their id.
export interface CtfSeatPicks { team?: string; ability?: string }

const seatOf = (room: RoomView, slot: number): ParticipantView | undefined => room.participants.find(p => p.slot === slot);

// Whose picks a side uses: a human player's own, else the room config (bots, empty seats, demos).
function ownPicks(room: RoomView, side: Side): ParticipantView | null {
  const rc = room.config as CtfRoomConfig;
  const p = seatOf(room, side === 'A' ? 0 : 1);
  return !rc.demo && p && p.type !== 'bot' ? p : null;
}

// The game's config with each player's own picks in it: what the host starts the game with.
export function ctfConfigWithPicks(room: RoomView): CtfRoomConfig {
  const rc: CtfRoomConfig = { ...CTF_ROOM_DEFAULT_CONFIG, ...(room.config as CtfRoomConfig) };
  if (leagueOfRoom(room)) return rc;   // a league game: the armies come from the league
  for (const side of ['A', 'B'] as Side[]) {
    const p = ownPicks(room, side);
    const picks = p ? (room.seatConfig[p.id] as CtfSeatPicks | undefined) : undefined;
    if (!p) continue;
    if (side === 'A') { rc.teamA = picks?.team ?? ''; rc.abilityA = picks?.ability ?? ''; }
    else { rc.teamB = picks?.team ?? ''; rc.abilityB = picks?.ability ?? ''; }
  }
  return rc;
}

function teamName(code: string): string {
  if (!code) return BUILT_IN_TEAMS[0].name;
  const built = BUILT_IN_TEAMS.find(t => t.code === code);
  if (built) return built.name;
  try { return decodeTeam(code).name; } catch { return 'Unreadable team (the standard squad plays)'; }
}

export default function CtfRoomOptions({ room, myId, isLeader, setConfig, setSeatConfig }: {
  room: RoomView; myId: string; isLeader: boolean;
  setConfig: (c: Record<string, unknown>) => void;
  setSeatConfig: (c: Record<string, unknown>) => void;
}) {
  const rc: CtfRoomConfig = { ...CTF_ROOM_DEFAULT_CONFIG, ...(room.config as CtfRoomConfig) };
  const merged = ctfConfigWithPicks(room);
  const [library, setLibrary] = React.useState<SavedTeam[]>(loadSavedTeams);
  const [all, setAll] = React.useState(false);
  const host = room.participants.find(p => p.isLeader);

  const league = leagueOfRoom(room);
  if (league) {
    // Both armies in full (with what each has gained in the league), so you see before the game what's
    // different about them.
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 12, alignItems: 'start' }}>
        {([['A', league.blue], ['B', league.red]] as const).map(([side, s]) => (
          <div key={side} style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, display: 'grid', gap: 6, minWidth: 0 }}>
            <span className="site-kicker" style={{ color: SIDE_COLOR[side] }}>{SIDE_NAME[side]} · {s.name}{s.bot ? ' (bot)' : ''}</span>
            <strong>{s.army}</strong>
            {s.armyData?.units?.length
              ? <ArmyTable player={{ name: s.name, army: s.armyData } as LeaguePlayer} />
              : <span className="site-muted site-small">{teamName(s.code)}</span>}
          </div>
        ))}
        <p className="site-muted site-small" style={{ gridColumn: '1 / -1' }}>League settings: {gameOptionsText(league.options)}.</p>
      </div>
    );
  }

  const sideBox = (side: Side) => {
    const seat = rc.demo ? host : seatOf(room, side === 'A' ? 0 : 1);
    const own = ownPicks(room, side);
    const mine = own ? own.id === myId : isLeader;
    const team = side === 'A' ? merged.teamA ?? '' : merged.teamB ?? '';
    const ability = side === 'A' ? merged.abilityA ?? '' : merged.abilityB ?? '';
    const set = (patch: CtfSeatPicks) => {
      if (own) setSeatConfig({ ...(room.seatConfig[own.id] ?? {}), ...patch } as Record<string, unknown>);
      else setConfig({ ...rc, ...(side === 'A'
        ? { ...(patch.team !== undefined && { teamA: patch.team }), ...(patch.ability !== undefined && { abilityA: patch.ability }) }
        : { ...(patch.team !== undefined && { teamB: patch.team }), ...(patch.ability !== undefined && { abilityB: patch.ability }) }) } as unknown as Record<string, unknown>);
    };
    const who = seat ? seat.name + (seat.type === 'bot' ? ' (bot)' : '') : 'Open seat';
    return (
      <div key={side} style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <span className="site-kicker" style={{ color: SIDE_COLOR[side] }}>{SIDE_NAME[side]} · {who}</span>
        {mine ? (
          <>
            <TeamPicker side={SIDE_NAME[side]} name={`team${side}`} value={team} library={library} onLibrary={setLibrary} onChange={code => set({ team: code })} />
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--muted)' }}>Army ability
              <select value={ability} onChange={e => set({ ability: e.target.value })}>
                <option value="">None</option>
                {(Object.keys(ABILITY_NAMES) as AbilityId[]).map(id => <option key={id} value={id}>{ABILITY_NAMES[id]}</option>)}
              </select>
            </label>
            {ability && <small className="site-muted">{abilityText[ability as AbilityId]}</small>}
            {!own && seat?.type === 'bot' && <small className="site-muted">You pick for the bot.</small>}
          </>
        ) : (
          <>
            <span className="site-muted site-small" style={{ marginTop: -4 }}>{own ? `${own.name} picks these` : 'The host picks these'}</span>
            <span><span className="site-muted">Team:</span> {teamName(team)}</span>
            <span><span className="site-muted">Army ability:</span> {ability ? ABILITY_NAMES[ability as AbilityId] : 'None'}</span>
          </>
        )}
      </div>
    );
  };

  const centre = rc.outposts === 'none' ? null : rc.centre ? EFFECT_NAMES[rc.centre as EffectId] ?? rc.centre : 'Random';
  const chips = [
    rc.outposts === 'centre' ? `${centre} center` : OUTPOST_NAMES[rc.outposts ?? 'centre'],
    `Flag weight ${rc.flagWeight ?? DEFAULT_CONFIG.carrierMovePenalty}`,
    rc.prisons ? 'Prisons on' : 'Prisons off',
    ...((rc.dominationCells ?? 0) > 0 ? [`Domination ${rc.dominationCells}`] : []),
    ...((rc.mercyRounds ?? 0) > 0 ? [`Mercy after ${rc.mercyRounds}`] : []),
    ...(rc.demo ? ['Demo: the host plays both sides'] : []),
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {sideBox('A')}{sideBox('B')}
      </div>
      <p className="site-muted" style={{ fontSize: 14 }}>Each player picks their own team and army ability. The map settings are the host's.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <span className="site-muted" style={{ fontSize: 14 }}>Map:</span>
        {chips.map(c => <span key={c} style={{ background: 'var(--bg)', borderRadius: 999, padding: '4px 12px', fontSize: 14 }}>{c}</span>)}
        {isLeader && <button type="button" className="site-btn site-btn-small" aria-expanded={all} onClick={() => setAll(v => !v)}>{all ? 'Hide settings' : 'All settings'}</button>}
      </div>
      {isLeader && all && <CtfSettings config={rc} onChange={c => setConfig(c as unknown as Record<string, unknown>)} hideTeams />}
    </>
  );
}
